import { Route } from '@angular/router'
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client'
import { AuthCallbackComponent } from './auth/auth-callback.component'
import { unsavedChangesGuard } from './pages/events/unsaved-changes.guard'

export const appRoutes: Route[] = [
  // Must stay unguarded: this is where Authentik redirects back to.
  { path: 'auth/callback', component: AuthCallbackComponent },

  // All guarded routes sit inside the layout shell
  {
    path: '',
    canActivate: [autoLoginPartialRoutesGuard],
    loadComponent: () => import('./shared/components/layout/layout.component').then(m => m.LayoutComponent),
    children: [
      {
        path: 'home',
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
        title: 'Dashboard - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.dashboard' },
      },
      {
        path: 'stores',
        loadComponent: () => import('./pages/stores/stores.component').then(m => m.StoresComponent),
        title: 'Stores - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.stores' },
      },
      {
        path: 'stores/:id',
        loadComponent: () => import('./pages/store-details/store-details.component').then(m => m.StoreDetailsComponent),
        title: 'Store Details - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.storeDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.stores', url: '/stores' }],
        },
      },
      {
        path: 'verification-requests',
        loadComponent: () => import('./pages/verification-requests/verification-requests.component').then(m => m.VerificationRequestsComponent),
        title: 'Verification Requests - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.verificationRequests' },
      },
      {
        path: 'verification-requests/:id',
        loadComponent: () =>
          import('./pages/verification-requests/verification-request-details/verification-request-details.component').then(
            m => m.VerificationRequestDetailsComponent,
          ),
        title: 'Verification Request Details - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.verificationRequestDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.verificationRequests', url: '/verification-requests' }],
        },
      },
      {
        path: 'help-center',
        loadComponent: () => import('./pages/help-center/help-center.component').then(m => m.HelpCenterComponent),
        title: 'Help Center - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.helpCenter' },
      },
      // Literal `articles` routes MUST come before `help-center/:categoryId`
      // below — otherwise the router matches "articles" as a categoryId.
      {
        path: 'help-center/articles',
        loadComponent: () =>
          import('./pages/help-center/help-center-articles/help-center-articles.component').then(m => m.HelpCenterArticlesComponent),
        title: 'Help Center Articles - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.helpCenterArticles',
          parentBreadcrumbs: [{ label: 'breadcrumb.helpCenter', url: '/help-center' }],
        },
      },
      {
        path: 'help-center/articles/new',
        loadComponent: () =>
          import('./pages/help-center/help-center-article-edit/help-center-article-edit.component').then(m => m.HelpCenterArticleEditComponent),
        title: 'New Help Center Article - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.helpCenterArticleEdit',
          parentBreadcrumbs: [
            { label: 'breadcrumb.helpCenter', url: '/help-center' },
            { label: 'breadcrumb.helpCenterArticles', url: '/help-center/articles' },
          ],
        },
      },
      {
        path: 'help-center/articles/:articleId',
        loadComponent: () =>
          import('./pages/help-center/help-center-article-edit/help-center-article-edit.component').then(m => m.HelpCenterArticleEditComponent),
        title: 'Edit Help Center Article - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.helpCenterArticleEdit',
          parentBreadcrumbs: [
            { label: 'breadcrumb.helpCenter', url: '/help-center' },
            { label: 'breadcrumb.helpCenterArticles', url: '/help-center/articles' },
          ],
        },
      },
      {
        path: 'help-center/:categoryId',
        loadComponent: () => import('./pages/help-center/help-center-items/help-center-items.component').then(m => m.HelpCenterItemsComponent),
        title: 'Help Center Items - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.helpCenterItems',
          parentBreadcrumbs: [{ label: 'breadcrumb.helpCenter', url: '/help-center' }],
        },
      },
      {
        path: 'action-logs',
        loadComponent: () => import('./pages/action-logs/action-logs.component').then(m => m.ActionLogsComponent),
        title: 'Action Logs - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.actionLogs' },
      },
      {
        path: 'discount-codes',
        loadComponent: () => import('./pages/discount-codes/discount-codes.component').then(m => m.DiscountCodesComponent),
        title: 'Discount Codes - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.discountCodes' },
      },
      {
        path: 'promotions',
        loadComponent: () => import('./pages/promotions/promotions.component').then(m => m.PromotionsComponent),
        title: 'Promotions - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.promotions' },
      },
      {
        path: 'users/confirm',
        loadComponent: () => import('./pages/users/confirm-user.component').then(m => m.ConfirmUserComponent),
        title: 'Confirm User - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.users' },
      },
      {
        path: 'support-tickets',
        loadComponent: () => import('./pages/support-tickets/support-tickets.component').then(m => m.SupportTicketsComponent),
        title: 'Support Tickets - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.supportTickets' },
      },
      {
        path: 'support-tickets/:id',
        loadComponent: () => import('./pages/support-tickets/ticket-details/ticket-details.component').then(m => m.TicketDetailsComponent),
        title: 'Ticket Details - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.ticketDetails',
          parentBreadcrumbs: [{ label: 'breadcrumb.supportTickets', url: '/support-tickets' }],
        },
      },
      {
        path: 'failing-charges',
        loadComponent: () => import('./pages/failing-charges/failing-charges.component').then(m => m.FailingChargesComponent),
        title: 'Failing Charges - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.failingCharges' },
      },
      {
        path: 'renewal-queue',
        loadComponent: () => import('./pages/renewal-queue/renewal-queue.component').then(m => m.RenewalQueueComponent),
        title: 'Renewal Queue - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.renewalQueue' },
      },
      {
        path: 'moderation',
        loadComponent: () => import('./pages/moderation/moderation.component').then(m => m.ModerationComponent),
        title: 'Moderation - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.moderation' },
      },
      {
        path: 'affiliates',
        loadComponent: () => import('./pages/affiliates/affiliates.component').then(m => m.AffiliatesComponent),
        title: 'Affiliates - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.affiliates' },
      },
      {
        path: 'credit-anomalies',
        loadComponent: () => import('./pages/credit-anomalies/credit-anomalies.component').then(m => m.CreditAnomaliesComponent),
        title: 'Credit Anomalies - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.creditAnomalies' },
      },
      {
        path: 'partners',
        loadComponent: () => import('./pages/partners/partners.component').then(m => m.PartnersComponent),
        title: 'Partners - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.partners' },
      },
      {
        path: 'partners/:id',
        loadComponent: () => import('./pages/partners/partner-detail.component').then(m => m.PartnerDetailComponent),
        title: 'Partner - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.partnerDetail',
          parentBreadcrumbs: [{ label: 'breadcrumb.partners', url: '/partners' }],
        },
      },
      {
        path: 'partner-commissions',
        loadComponent: () => import('./pages/partners/partner-commissions.component').then(m => m.PartnerCommissionsComponent),
        title: 'Partner Commissions - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.partnerCommissions',
          parentBreadcrumbs: [{ label: 'breadcrumb.partners', url: '/partners' }],
        },
      },
      {
        path: 'partner-payouts',
        loadComponent: () => import('./pages/partners/partner-payouts.component').then(m => m.PartnerPayoutsComponent),
        title: 'Partner Payouts - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.partnerPayouts',
          parentBreadcrumbs: [{ label: 'breadcrumb.partners', url: '/partners' }],
        },
      },
      {
        path: 'partner-program',
        loadComponent: () => import('./pages/partners/partner-program.component').then(m => m.PartnerProgramComponent),
        title: 'Partner Program - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.partnerProgram',
          parentBreadcrumbs: [{ label: 'breadcrumb.partners', url: '/partners' }],
        },
      },
      {
        path: 'mobile-app',
        loadComponent: () => import('./pages/mobile-app/mobile-app.component').then(m => m.MobileAppComponent),
        title: 'Mobile App - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.mobileApp' },
      },
      {
        path: 'announcements',
        loadComponent: () => import('./pages/announcements/announcements.component').then(m => m.AnnouncementsComponent),
        title: 'Announcements - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.announcements' },
      },
      // Literal `new` MUST come before `announcements/:announcementId` below —
      // otherwise the router matches "new" as an announcementId.
      {
        path: 'announcements/new',
        loadComponent: () => import('./pages/announcements/announcement-add/announcement-add.component').then(m => m.AnnouncementAddComponent),
        title: 'New Announcement - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.announcementAdd',
          parentBreadcrumbs: [{ label: 'breadcrumb.announcements', url: '/announcements' }],
        },
      },
      {
        path: 'announcements/:announcementId/edit',
        loadComponent: () => import('./pages/announcements/announcement-edit/announcement-edit.component').then(m => m.AnnouncementEditComponent),
        title: 'Edit Announcement - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.announcementEdit',
          parentBreadcrumbs: [{ label: 'breadcrumb.announcements', url: '/announcements' }],
        },
      },
      {
        path: 'announcements/:announcementId',
        loadComponent: () => import('./pages/announcements/announcement-view/announcement-view.component').then(m => m.AnnouncementViewComponent),
        title: 'Announcement Details - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.announcementView',
          parentBreadcrumbs: [{ label: 'breadcrumb.announcements', url: '/announcements' }],
        },
      },
      {
        path: 'events',
        loadComponent: () => import('./pages/events/events-list/events-list.component').then(m => m.EventsListComponent),
        title: 'Events - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.events' },
      },
      // Literal `new` MUST come before `events/:eventId` below.
      {
        path: 'events/new',
        loadComponent: () => import('./pages/events/event-edit/event-edit.component').then(m => m.EventEditComponent),
        canDeactivate: [unsavedChangesGuard],
        title: 'New Event - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.eventAdd',
          parentBreadcrumbs: [{ label: 'breadcrumb.events', url: '/events' }],
        },
      },
      {
        path: 'events/:eventId',
        loadComponent: () => import('./pages/events/event-edit/event-edit.component').then(m => m.EventEditComponent),
        canDeactivate: [unsavedChangesGuard],
        title: 'Edit Event - TakeTheme Admin',
        data: {
          breadcrumb: 'breadcrumb.eventEdit',
          parentBreadcrumbs: [{ label: 'breadcrumb.events', url: '/events' }],
        },
      },
      {
        path: 'data-requests',
        loadComponent: () => import('./pages/data-requests/data-requests.component').then(m => m.DataRequestsComponent),
        title: 'Data Requests - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.dataRequests' },
      },
      {
        path: 'theme-moderation',
        loadComponent: () => import('./pages/theme-moderation/theme-moderation.component').then(m => m.ThemeModerationComponent),
        title: 'Theme Moderation - TakeTheme Admin',
        data: { breadcrumb: 'breadcrumb.themeModeration' },
      },
      { path: '', pathMatch: 'full', redirectTo: 'home' },
    ],
  },
  { path: '**', redirectTo: '' },
]
